import { createContext, useContext } from 'react';
import { useTenantSlug } from './session.tsx';
import type { Catalog, CatalogProduct, PriceRule } from './types.ts';

/**
 * The menu as the screens price it: price times (happy hour) and combo menus. The same rules
 * as the server (modules/products), so the price on a tile is the price on the bill; the
 * server prices every line again anyway.
 */

export interface LocalMoment {
  date: string;
  weekday: number;
  minutes: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function localMoment(at: Date, timeZone: string): LocalMoment {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, weekday: WEEKDAYS.indexOf(parts.weekday!), minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

function dayBefore(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function ruleOpen(rule: PriceRule, at: LocalMoment): boolean {
  if (!rule.active) return false;
  const start = minutesOf(rule.start_time);
  const end = minutesOf(rule.end_time);
  let day: { date: string; weekday: number } | null = null;
  if (start === end) day = at;
  else if (start < end) day = at.minutes >= start && at.minutes < end ? at : null;
  else if (at.minutes >= start) day = at;
  else if (at.minutes < end) day = { date: dayBefore(at.date), weekday: (at.weekday + 6) % 7 };
  if (!day || !rule.days.includes(day.weekday)) return false;
  if (rule.date_from && day.date < rule.date_from) return false;
  if (rule.date_to && day.date > rule.date_to) return false;
  return true;
}

export function ruleCovers(rule: PriceRule, product: Pick<CatalogProduct, 'id' | 'category'>): boolean {
  if (!rule.products.length && !rule.categories.length) return true;
  return rule.products.includes(product.id) || (!!product.category && rule.categories.includes(product.category));
}

export interface PriceNow {
  price: number;
  /** The price time that gives it, or null for the normal price. */
  rule: string | null;
}

export function priceNow(catalog: Pick<Catalog, 'price_rules' | 'time_zone'>, product: Pick<CatalogProduct, 'id' | 'category' | 'price'>, at: Date): PriceNow {
  const moment = localMoment(at, catalog.time_zone ?? 'UTC');
  let best: PriceNow = { price: product.price, rule: null };
  for (const r of catalog.price_rules ?? []) {
    if (!ruleOpen(r, moment) || !ruleCovers(r, product)) continue;
    const price = r.kind === 'percent' ? product.price - Math.round((product.price * r.percent) / 100) : r.price;
    if (price < best.price) best = { price, rule: r.name };
  }
  return best;
}

/** Splits a combo price over its items by their own prices, to the cent (like the server). */
export function splitPrice(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  const w = sum > 0 ? weights : weights.map(() => 1);
  const all = w.reduce((a, b) => a + b, 0);
  const exact = w.map((x) => (total * x) / all);
  const out = exact.map((x) => Math.floor(x));
  let left = total - out.reduce((a, b) => a + b, 0);
  const order = exact.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    out[i]! += 1;
    left--;
  }
  return out;
}

/** The address of a product's photo, or null when it has none. */
export function useImageUrl(): (p: Pick<CatalogProduct, 'id' | 'image'>) => string | null {
  const guest = useContext(MediaTenant);
  const own = useTenantSlug();
  const slug = guest ?? own;
  return (p) => (p.image && slug ? `/media/${slug}/products/${p.id}?v=${p.image}` : null);
}

/** On a guest's page nobody is signed in: the page says whose photos to show. */
export const MediaTenant = createContext<string | null>(null);

/** The groups of a combo with what each offers, in menu order. */
export function comboGroups(catalog: Catalog, comboId: string) {
  return (catalog.combo_groups ?? [])
    .filter((g) => g.combo === comboId)
    .sort((a, b) => a.sequence - b.sequence)
    .map((g) => ({
      ...g,
      items: (catalog.combo_items ?? [])
        .filter((i) => i.group === g.id)
        .sort((a, b) => a.sequence - b.sequence)
        .map((i) => ({ ...i, product: catalog.products.find((p) => p.id === i.product) }))
        .filter((i): i is typeof i & { product: CatalogProduct } => !!i.product),
    }));
}
